import Close from "@mui/icons-material/Close";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link as MuiLink,
  ListSubheader,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { type FC, useState } from "react";
import { Link } from "react-router";
import { usePermissions } from "../../../../../server/auth/use-permissions";
import type { DialogController } from "../../../../../utils/hooks/use-dialog";
import type { UUID } from "../../../../../utils/uuid";
import { personLabel } from "../../../../employees/server/supabase-person-service";
import { ReviewMessageField } from "./review-message-field";
import { REVIEWER_GROUPS, type ReviewerOption, useReviewerOptions } from "./use-reviewer-options";

export type SendForReviewSelection = {
  reviewer: ReviewerOption;
  message: string;
};

export const SendForReviewDialog: FC<{
  dialog: DialogController<unknown>;
  programId: UUID;
  currentReviewerId?: UUID | null;
  sending: boolean;
  onSend: (selection: SendForReviewSelection) => void;
}> = ({ dialog, ...props }) => (
  <Dialog
    open={dialog.open}
    onClose={dialog.handleClose}
    fullWidth
    maxWidth="sm"
    aria-labelledby="send-review-title"
  >
    <DialogTitle id="send-review-title">
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        Send for review
        <IconButton
          aria-label="Close send for review"
          data-analytics-id="moment-review-dialog-close"
          onClick={dialog.handleClose}
        >
          <Close />
        </IconButton>
      </Box>
    </DialogTitle>
    <SendForReviewContent onClose={dialog.handleClose} {...props} />
  </Dialog>
);

const SendForReviewContent: FC<{
  programId: UUID;
  currentReviewerId?: UUID | null;
  sending: boolean;
  onClose: () => void;
  onSend: (selection: SendForReviewSelection) => void;
}> = ({ programId, currentReviewerId, ...props }) => {
  const { data: reviewers, loading, error } = useReviewerOptions(programId);

  if (loading)
    return (
      <DialogContent>
        <Stack alignItems="center" sx={{ py: 2 }}>
          <CircularProgress />
        </Stack>
      </DialogContent>
    );

  return (
    <SendForReviewForm
      reviewers={reviewers ?? []}
      assigned={reviewers?.find(({ id }) => id === currentReviewerId) ?? null}
      error={error}
      {...props}
    />
  );
};

const SendForReviewForm: FC<{
  reviewers: ReviewerOption[];
  assigned: ReviewerOption | null;
  error: Error | null;
  sending: boolean;
  onClose: () => void;
  onSend: (selection: SendForReviewSelection) => void;
}> = ({ reviewers, assigned, error, sending, onClose, onSend }) => {
  const [reviewer, setReviewer] = useState<ReviewerOption | null>(assigned);
  const [message, setMessage] = useState("");
  const { super_admin } = usePermissions();

  return (
    <>
      <DialogContent>
        <Stack spacing={2}>
          {assigned ? (
            <Typography variant="body2">
              <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                {`${assigned.first_name} ${assigned.last_name}`}
              </Typography>
              {" is already reviewing this Communication. Sending it to somebody else moves the "}
              {`review to them, and ${assigned.first_name} loses access to it.`}
            </Typography>
          ) : (
            <Typography variant="body2">
              Select a reviewer below. They will be able to comment on and change the status of your
              Communication.
            </Typography>
          )}
          <Typography variant="body2">
            You can still edit and publish the Communication while it’s in review – this just allows
            visibility and feedback within the app. If the reviewer has the correct permissions,
            they will also be able to edit and publish the Communication on your behalf.
          </Typography>

          <Autocomplete
            value={reviewer}
            options={reviewers}
            groupBy={(option) => option.group}
            getOptionLabel={personLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText={error ? "Couldn’t load reviewers" : "No reviewers available"}
            onChange={(_, value) => setReviewer(value)}
            renderOption={(renderProps, option) => (
              <Box component="li" {...renderProps} key={option.id}>
                <Typography variant="body2">
                  {option.first_name} {option.last_name}{" "}
                  <Typography component="span" variant="body2" color="text.secondary">
                    ({option.email})
                  </Typography>
                </Typography>
              </Box>
            )}
            renderGroup={({ key, group, children }) => (
              <li key={key}>
                <ListSubheader component="div">
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, textTransform: "uppercase" }}
                  >
                    {group}
                  </Typography>
                  {group === REVIEWER_GROUPS.reviewers && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ textTransform: "none" }}
                    >
                      {" "}
                      (Cannot edit or publish
                      {super_admin ? (
                        <>
                          {" – add more reviewers in "}
                          <MuiLink component={Link} to="/manage/employees/permissions">
                            settings
                          </MuiLink>
                        </>
                      ) : null}
                      )
                    </Typography>
                  )}
                </ListSubheader>
                <Box component="ul" sx={{ p: 0 }}>
                  {children}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select reviewer"
                placeholder="Search reviewers"
                error={!!error}
                required
              />
            )}
          />

          <ReviewMessageField
            label="Add a message for the reviewer (optional)"
            value={message}
            onChange={setMessage}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button data-analytics-id="moment-review-dialog-cancel" onClick={onClose}>
          Cancel
        </Button>
        <Button
          data-analytics-id="moment-review-dialog-send"
          loading={sending}
          disabled={!reviewer}
          onClick={() => reviewer && onSend({ reviewer, message: message.trim() })}
        >
          Send for review
        </Button>
      </DialogActions>
    </>
  );
};

export const SaveChangesBeforeReviewDialog: FC<{
  dialog: DialogController<unknown>;
  saving: boolean;
  onSave: () => void;
}> = ({ dialog, saving, onSave }) => (
  <Dialog
    open={dialog.open}
    onClose={saving ? undefined : dialog.handleClose}
    fullWidth
    maxWidth="sm"
  >
    <DialogTitle>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        Save changes to send for review
        <IconButton
          aria-label="Close save changes warning"
          data-analytics-id="moment-review-save-warning-close"
          disabled={saving}
          onClick={dialog.handleClose}
        >
          <Close />
        </IconButton>
      </Box>
    </DialogTitle>
    <DialogContent>
      <Stack spacing={2}>
        <Typography variant="body2">
          Reviewers always see the most recently saved version, so this Communication has to be
          saved before it can be sent.
        </Typography>
        <Typography variant="body2">
          You can continue making edits after sending it for review, and any changes you save will
          be reflected in what the reviewer sees.
        </Typography>
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button
        data-analytics-id="moment-review-save-warning-cancel"
        disabled={saving}
        onClick={dialog.handleClose}
      >
        Cancel
      </Button>
      <Button data-analytics-id="moment-review-save-changes" loading={saving} onClick={onSave}>
        Save changes
      </Button>
    </DialogActions>
  </Dialog>
);
